import React, { useEffect, useState } from 'react';
import { Phone, CheckCircle, Send } from 'lucide-react';
export const HowItWorks = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('process');
      if (element) {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight - 100) {
          setIsVisible(true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep(prev => (prev + 1) % 3);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);
  const steps = [{
    icon: <Phone size={28} className="text-white" data-id="element-197" />,
    title: 'Call',
    description: 'Our professionals make strategic cold calls to potential leads, using proven scripts and persuasive techniques.',
    number: '1'
  }, {
    icon: <CheckCircle size={28} className="text-white" data-id="element-198" />,
    title: 'Qualify',
    description: 'Each prospect undergoes thorough qualification to ensure they meet your specific criteria and are worth your attention.',
    number: '2'
  }, {
    icon: <Send size={28} className="text-white" data-id="element-199" />,
    title: 'Deliver',
    description: 'Qualified leads are delivered directly to your sales team, ready for follow-up and conversion.',
    number: '3'
  }];
  return <section id="process" className="relative py-20 bg-gradient-to-b from-gray-900 to-black w-full" data-id="element-200">
      {/* Animated background elements */}
      <div className="absolute inset-0" data-id="element-201">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-blue-primary/5 to-transparent" data-id="element-202"></div>
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-blue-primary/5 to-transparent" data-id="element-203"></div>
        {/* Animated lines */}
        <div className="absolute inset-0 overflow-hidden opacity-20" data-id="element-204">
          <div className="absolute h-0.5 w-1/3 bg-blue-primary/50 top-1/4 left-0 animate-movingLine" style={{
          animationDuration: '15s'
        }} data-id="element-205"></div>
          <div className="absolute h-0.5 w-1/4 bg-blue-light/50 top-1/2 right-0 animate-movingLineReverse" style={{
          animationDuration: '12s'
        }} data-id="element-206"></div>
          <div className="absolute h-0.5 w-1/5 bg-blue-primary/50 bottom-1/3 left-1/4 animate-movingLine" style={{
          animationDuration: '18s'
        }} data-id="element-207"></div>
        </div>
      </div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-208">
        <div className="text-center mb-16" data-id="element-209">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fadeIn" data-id="element-210">
            Our{' '}
            <span className="text-blue-light relative" data-id="element-211">
              Process
              <span className="absolute -inset-1 bg-blue-light/10 blur-md -z-10 rounded-lg" data-id="element-212"></span>
            </span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto animate-fadeIn" style={{
          animationDelay: '0.2s'
        }} data-id="element-213">
            Our three-step process ensures efficient and effective lead
            generation for your business success.
          </p>
        </div>
        <div className={`relative ${isVisible ? 'opacity-100' : 'opacity-0'} transition-opacity duration-1000`} data-id="element-214">
          {/* Progress bar */}
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-1 bg-gray-800 transform -translate-y-1/2 z-0" data-id="element-215">
            <div className="h-full bg-gradient-to-r from-blue-primary to-blue-light transition-all duration-700 relative" style={{
            width: `${(activeStep + 1) * 33.33}%`
          }} data-id="element-216">
              <div className="absolute right-0 top-1/2 w-3 h-3 bg-blue-light rounded-full transform -translate-y-1/2 animate-pulse" data-id="element-217"></div>
            </div>
          </div>
          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10" data-id="element-218">
            {steps.map((step, index) => <div key={index} className={`relative transition-all duration-700 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} ${activeStep === index ? 'scale-105' : 'scale-100'}`} style={{
            transitionDelay: `${index * 200}ms`
          }} data-id="element-219">
                <div className={`bg-gray-900 border ${activeStep === index ? 'border-blue-light' : 'border-gray-800'} rounded-lg p-8 text-center transition-all duration-300 relative group`} data-id="element-220">
                  {/* Animated highlight when active */}
                  {activeStep === index && <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-primary to-blue-light rounded-lg blur opacity-20 animate-pulse" data-id="element-221"></div>}
                  {/* Icon circle replacing number */}
                  <div className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center relative ${activeStep === index ? 'bg-gradient-to-r from-blue-primary to-blue-light' : 'bg-gray-800'} transition-all duration-300`} data-id="element-222">
                    {activeStep === index && <div className="absolute -inset-1 bg-blue-light/20 rounded-full animate-ping opacity-75" data-id="element-223"></div>}
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3" data-id="element-224">{step.title}</h3>
                  <p className="text-gray-400" data-id="element-225">{step.description}</p>
                </div>
                {/* Mobile progress indicator */}
                {index < steps.length - 1 && <div className="md:hidden flex justify-center my-4" data-id="element-226">
                    <div className="w-1 h-8 bg-blue-light" data-id="element-227"></div>
                  </div>}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};